<template>
  <div class="rise-in card card-pad">
    <!-- Centered Header Titles matching upload_original.png -->
    <div class="text-center mb-8 mt-2">
      <h2
        class="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2"
      >
        Upload Measurement Files
      </h2>
      <h1 class="text-2xl sm:text-3xl font-bold text-ink">
        Please upload breath measurement files now.
      </h1>
    </div>

    <!-- Side-by-Side Cards Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
      <div
        v-for="slot in slots"
        :key="slot.key"
        class="bg-[#faf9fc] border border-purple-100 rounded-2xl p-8 shadow-sm flex flex-col items-center justify-between min-h-[460px] relative"
      >
        <!-- Top Green Icon Circle with Symbol -->
        <div class="flex flex-col items-center gap-4 w-full">
          <div
            class="w-16 h-16 rounded-full bg-[#2ea16c] flex items-center justify-center text-white text-4xl font-black select-none shadow-sm"
          >
            {{ slot.iconText }}
          </div>
          <h3 class="text-sm font-bold text-brand-purple-dark text-center mt-2">
            Select {{ slot.label }} RAW file to upload:
          </h3>
        </div>

        <!-- Drag & Drop / Click Upload Box for RAW File -->
        <div class="w-full flex-1 flex flex-col justify-center my-6">
          <div
            v-if="!files[`${slot.key}_raw`]"
            class="upload-dropzone w-full border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#831f82] transition-colors duration-150 bg-white"
            @click="$refs[`file_${slot.key}_raw`][0].click()"
          >
            <input
              :ref="`file_${slot.key}_raw`"
              type="file"
              class="hidden"
              @change="onPick(slot.key, 'raw', $event)"
            />
            <font-awesome-icon
              v-if="progress[`${slot.key}_raw`] == null"
              icon="upload"
              class="text-slate-400 text-3xl"
            />
            <font-awesome-icon
              v-else
              icon="circle-notch"
              spin
              class="text-brand-purple text-3xl"
            />
            <span class="text-xs text-slate-500 font-semibold text-center">
              {{
                progress[`${slot.key}_raw`] != null
                  ? `Uploading... ${progress[`${slot.key}_raw`]}%`
                  : 'Select local RAW-File to upload'
              }}
            </span>
          </div>

          <!-- Uploaded Files List -->
          <div
            v-if="files[`${slot.key}_raw`] || files[`${slot.key}_co2`]"
            class="w-full bg-[#f3f0f7] border border-purple-100 rounded-xl p-4 flex flex-col gap-3"
          >
            <!-- RAW file row -->
            <div
              v-if="files[`${slot.key}_raw`]"
              class="flex items-center justify-between text-sm text-slate-700 bg-white px-3 py-2 rounded-lg border border-purple-50/50 shadow-sm"
            >
              <a
                :href="files[`${slot.key}_raw`]"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-2 hover:underline font-medium text-brand-purple-dark"
              >
                <font-awesome-icon
                  icon="file-archive"
                  class="text-brand-purple"
                />
                <span>visit_{{ visitNumber }}_{{ slot.key }}.raw</span>
              </a>
              <span
                class="text-xs text-green-600 font-bold uppercase tracking-wider"
                >Uploaded</span
              >
            </div>

            <!-- CO2 file row -->
            <div
              v-if="files[`${slot.key}_co2`]"
              class="flex items-center justify-between text-sm text-slate-700 bg-white px-3 py-2 rounded-lg border border-purple-50/50 shadow-sm"
            >
              <a
                :href="files[`${slot.key}_co2`]"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-2 hover:underline font-medium text-brand-purple-dark"
              >
                <font-awesome-icon icon="file" class="text-brand-purple" />
                <span>visit_{{ visitNumber }}_{{ slot.key }}.txt</span>
              </a>
              <span
                class="text-xs text-green-600 font-bold uppercase tracking-wider"
                >Uploaded</span
              >
            </div>
          </div>

          <!-- CO2 Data Upload Box (appears after clicking + ADD CO2 DATA) -->
          <div
            v-if="addCo2[slot.key] && !files[`${slot.key}_co2`]"
            class="w-full mt-4 flex flex-col gap-2"
          >
            <span class="text-xs font-semibold text-brand-purple-dark"
              >Select CO2 data file to upload:</span
            >
            <div
              class="upload-dropzone w-full border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#831f82] transition-colors duration-150 bg-white"
              @click="$refs[`file_${slot.key}_co2`][0].click()"
            >
              <input
                :ref="`file_${slot.key}_co2`"
                type="file"
                class="hidden"
                @change="onPick(slot.key, 'co2', $event)"
              />
              <font-awesome-icon
                v-if="progress[`${slot.key}_co2`] == null"
                icon="upload"
                class="text-slate-400 text-2xl"
              />
              <font-awesome-icon
                v-else
                icon="circle-notch"
                spin
                class="text-brand-purple text-2xl"
              />
              <span class="text-xs text-slate-500 font-semibold text-center">
                {{
                  progress[`${slot.key}_co2`] != null
                    ? `Uploading... ${progress[`${slot.key}_co2`]}%`
                    : 'Select local CO2 file to upload'
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Add CO2 Data Button -->
        <button
          v-if="!addCo2[slot.key] && !files[`${slot.key}_co2`]"
          class="co2-btn flex items-center justify-center gap-2 px-4 py-2 font-bold rounded-lg text-xs uppercase tracking-wide cursor-pointer focus:outline-none transition-colors"
          @click="$set(addCo2, slot.key, true)"
        >
          <font-awesome-icon icon="plus" />
          Add CO2 Data
        </button>
      </div>
    </div>

    <!-- Error notice -->
    <p v-if="error" class="text-red-600 text-sm mt-3 text-center font-semibold">
      {{ error }}
    </p>

    <!-- Bottom Footer Row with BACK Button matching upload_original.png -->
    <div class="flex justify-end mt-8">
      <router-link
        class="back-btn px-8 py-3 font-bold rounded-lg text-sm uppercase tracking-wider text-center no-underline cursor-pointer focus:outline-none transition-colors"
        :to="{ name: 'PatientOverview', params: { projectSlug, patientId } }"
      >
        Back
      </router-link>
    </div>
  </div>
</template>

<script>
const STORAGE_URL = process.env.VUE_APP_STORAGE_URL || '/storage'
const API_URL = process.env.VUE_APP_API_URL || '/api'

export default {
  name: 'Upload',
  data() {
    return {
      slots: [
        { key: 'pos', label: 'positive', iconText: '+' },
        { key: 'neg', label: 'negative', iconText: '-' },
      ],
      addCo2: {
        pos: false,
        neg: false,
      },
      progress: {},
      files: {},
      error: null,
      loading: false,
    }
  },
  computed: {
    projectSlug() {
      return this.$route.params.projectSlug
    },
    patientId() {
      return this.$route.params.patientId
    },
    visitNumber() {
      return this.$route.params.visitNumber || '1'
    },
    formKey() {
      return `visit${this.visitNumber}-data`
    },
  },
  created() {
    this.fetchForm()
  },
  methods: {
    async fetchForm() {
      this.loading = true
      try {
        const { rest } = await import('helpers/api')
        const current = await rest.getForm(this.patientId, this.formKey)
        if (current && current.files) {
          this.loadFormFiles(current.files)
          if (current.files.pos && current.files.pos.co2) {
            this.$set(this.addCo2, 'pos', true)
          }
          if (current.files.neg && current.files.neg.co2) {
            this.$set(this.addCo2, 'neg', true)
          }
        }
      } catch (e) {
        this.error = 'Failed to load existing files.'
      } finally {
        this.loading = false
      }
    },
    loadFormFiles(files) {
      const filesMap = {}
      if (files) {
        if (files.pos) {
          if (files.pos.raw)
            filesMap.pos_raw = `${STORAGE_URL}/${files.pos.raw}`
          if (files.pos.co2)
            filesMap.pos_co2 = `${STORAGE_URL}/${files.pos.co2}`
        }
        if (files.neg) {
          if (files.neg.raw)
            filesMap.neg_raw = `${STORAGE_URL}/${files.neg.raw}`
          if (files.neg.co2)
            filesMap.neg_co2 = `${STORAGE_URL}/${files.neg.co2}`
        }
      }
      this.files = filesMap
    },
    storagePath(slotKey, fileType) {
      const ext = fileType === 'raw' ? 'raw' : 'txt'
      return `${this.projectSlug}/${this.patientId}/visit_${this.visitNumber}_${slotKey}.${ext}`
    },
    async onPick(slotKey, fileType, ev) {
      const file = ev.target.files && ev.target.files[0]
      if (!file) return
      this.error = null

      const progressKey = `${slotKey}_${fileType}`
      this.$set(this.progress, progressKey, 0)
      try {
        const path = this.storagePath(slotKey, fileType)
        await this.upload(file, path, p =>
          this.$set(this.progress, progressKey, p)
        )

        const { rest } = await import('helpers/api')
        const current = (await rest.getForm(this.patientId, this.formKey)) || {
          files: {},
        }

        const slotFiles = Object.assign(
          {},
          current.files && current.files[slotKey],
          {
            [fileType]: path,
          }
        )
        const filesPatch = Object.assign({}, current.files, {
          [slotKey]: slotFiles,
        })

        await rest.updateForm(this.patientId, this.formKey, {
          files: filesPatch,
          status: 'finished',
        })

        this.loadFormFiles(filesPatch)
        this.$set(this.progress, progressKey, null)
      } catch (e) {
        this.error = e.message || 'Upload failed.'
        this.$set(this.progress, progressKey, null)
      }
    },
    upload(file, path, onProgress) {
      return new Promise((resolve, reject) => {
        const form = new FormData()
        form.append('path', path)
        form.append('file', file)
        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${API_URL}/upload`)
        try {
          const s = JSON.parse(localStorage.getItem('dbi.session') || 'null')
          if (s && s.access_token)
            xhr.setRequestHeader('Authorization', `Bearer ${s.access_token}`)
        } catch (e) {
          /* ignore */
        }
        xhr.upload.onprogress = e => {
          if (e.lengthComputable)
            onProgress(Math.round((e.loaded / e.total) * 100))
        }
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error('HTTP ' + xhr.status))
        xhr.onerror = () => reject(new Error('network error'))
        xhr.send(form)
      })
    },
  },
}
</script>

<style scoped>
.brand-purple-text {
  color: #831f82;
}
.brand-purple-bg {
  background-color: #831f82;
}
.text-brand-purple {
  color: #831f82;
}
.text-brand-purple-dark {
  color: #511b5e;
}
.back-btn {
  background-color: #831f82;
  color: white;
}
.back-btn:hover {
  background-color: #6a1b7a;
}
.co2-btn {
  background-color: #6c7d95;
  color: white;
  width: auto;
  border: 0;
}
.co2-btn:hover {
  background-color: #5a6a7e;
}
.upload-dropzone {
  transition: all 0.15s ease-in-out;
}
.upload-dropzone:hover {
  background-color: #fcfaff;
}
</style>
